
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Patient } from '@/types/doctor';

interface UseCaseFileProps {
  patientId?: string;
  caseId?: string;
}

export function useCaseFile({ patientId, caseId }: UseCaseFileProps) {
  const [patient, setPatient] = useState<Patient>({} as Patient);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (patientId) {
      fetchPatientData(patientId);
    }
  }, [patientId]);

  const fetchPatientData = async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching patient data for ID: ${id}`);
      
      // First try to get data from patient_profiles_view which combines patient and profile data
      const { data: viewData, error: viewError } = await supabase
        .from('patient_profiles_view')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (!viewError && viewData) {
        console.log("Found patient in profile view:", viewData);
        const formattedPatient = formatPatientFromView(viewData);
        setPatient(formattedPatient);
        setIsLoading(false);
        return;
      }
      
      console.log("Patient not found in view, trying direct patient table");
      
      // Fallback to directly querying the patients table
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select(`
          id,
          profile_id,
          date_of_birth,
          gender,
          address_street,
          address_city,
          address_state,
          address_country,
          medical_conditions,
          allergies,
          created_at,
          updated_at,
          status,
          progress
        `)
        .eq('id', id)
        .maybeSingle();
        
      if (patientError) {
        console.error("Error fetching patient:", patientError);
        throw patientError;
      }
      
      if (!patientData) {
        console.error("No patient data found with ID:", id);
        throw new Error('Patient not found');
      }
      
      console.log("Patient data found:", patientData);
      
      let profileData = null;
      
      // Fetch profile data separately if profile_id exists
      if (patientData.profile_id) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select(`
            id,
            first_name,
            last_name,
            email,
            phone_number,
            avatar_url
          `)
          .eq('id', patientData.profile_id)
          .maybeSingle();
        
        if (profileError) {
          console.error("Error fetching profile:", profileError);
          // Don't throw error for profile - continue with null profileData
        } else if (profile) {
          profileData = profile;
          console.log("Profile data found:", profileData);
        }
      } else {
        console.log("No profile_id for patient:", id);
      }
      
      const formattedPatient = formatPatientData(patientData, profileData);
      console.log("Formatted patient data:", formattedPatient);
      setPatient(formattedPatient);
    } catch (error: any) {
      console.error('Error fetching patient data:', error);
      setError(error.message);
      toast({
        title: "Error loading patient",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatPatientFromView = (viewData: any): Patient => {
    // Format name
    const name = `${viewData.first_name || ''} ${viewData.last_name || ''}`.trim() || 'Unknown Patient';
    
    // Format address
    const addressStr = [
      viewData.address_street,
      viewData.address_city,
      viewData.address_state,
      viewData.address_country
    ].filter(Boolean).join(', ') || 'No address provided';
    
    // Format medical conditions
    let condition = 'No medical conditions';
    let medicalConditions = viewData.medical_conditions;
    
    if (medicalConditions) {
      // Try to parse if it's a JSON string
      if (typeof medicalConditions === 'string' && (medicalConditions.startsWith('[') || medicalConditions.startsWith('{'))) {
        try {
          medicalConditions = JSON.parse(medicalConditions);
        } catch (e) {
          console.log("Failed to parse medical_conditions:", e);
        }
      }
      
      if (Array.isArray(medicalConditions) && medicalConditions.length > 0) {
        condition = String(medicalConditions[0]);
      } else if (typeof medicalConditions === 'string' && medicalConditions.trim() !== '') {
        condition = medicalConditions;
      } else if (typeof medicalConditions === 'object' && medicalConditions !== null) {
        const conditionValues = Object.values(medicalConditions);
        if (conditionValues.length > 0) {
          condition = String(conditionValues[0]);
        }
      }
    }
    
    const formattedPatient: Patient = {
      id: viewData.id,
      name,
      condition,
      profile_id: viewData.profile_id,
      createdAt: viewData.created_at,
      status: viewData.status || 'new',
      progress: viewData.progress,
      profile: {
        email: '',
        phone_number: '',
        medicalConditions: medicalConditions || []
      }
    };

    // Add all patient case-related fields that might be present
    const caseFields = [
      'title', 'description', 'date_of_birth', 'gender',
      'has_diabetes', 'has_hypertension', 'has_heart_disease',
      'has_asthma', 'has_allergies', 'has_thyroid_disorder',
      'has_hepatitis', 'has_hiv_aids', 'other_conditions'
    ];
    
    caseFields.forEach(field => {
      if (viewData[field] !== undefined) {
        // @ts-ignore - adding dynamic properties
        formattedPatient[field] = viewData[field];
      }
    });
    
    return formattedPatient;
  };
  
  const formatName = (patientData: any, profileData: any): string => {
    if (profileData) {
      return `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || 'Unknown Patient';
    }
    return 'Unknown Patient';
  };
  
  const formatAddress = (patientData: any): string => {
    const parts = [
      patientData.address_street,
      patientData.address_city,
      patientData.address_state,
      patientData.address_country
    ].filter(Boolean);
    
    return parts.join(', ') || 'No address provided';
  };
  
  const calculateAge = (dateOfBirth: string): number | null => {
    if (!dateOfBirth) return null;
    
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };
  
  const formatPatientData = (patientData: any, profileData: any) => {
    // Format medical conditions for display
    let condition: string = 'No medical conditions';
    let medicalConditions = patientData.medical_conditions;
    
    // Handle different formats of medical_conditions
    if (medicalConditions) {
      // Try to parse if it's a JSON string
      if (typeof medicalConditions === 'string' && (medicalConditions.startsWith('[') || medicalConditions.startsWith('{'))) {
        try {
          medicalConditions = JSON.parse(medicalConditions);
        } catch (e) {
          console.log("Failed to parse medical_conditions:", e);
        }
      }
      
      if (Array.isArray(medicalConditions) && medicalConditions.length > 0) {
        condition = String(medicalConditions[0]);
      } else if (typeof medicalConditions === 'string' && medicalConditions.trim() !== '') {
        condition = medicalConditions;
      } else if (typeof medicalConditions === 'object' && medicalConditions !== null) {
        const conditionValues = Object.values(medicalConditions);
        if (conditionValues.length > 0) {
          condition = String(conditionValues[0]);
        }
      }
    }
    
    const formattedPatient: Patient = {
      ...patientData,
      name: formatName(patientData, profileData),
      condition: condition,
      createdAt: patientData.created_at || new Date().toISOString(),
      profile: profileData ? {
        ...profileData,
        email: profileData.email || '',
        phone_number: profileData.phone_number || '',
        medicalConditions: medicalConditions || []
      } : {
        email: '',
        phone_number: '',
        medicalConditions: medicalConditions || []
      }
    };
    
    // Calculate age if date of birth exists
    if (patientData.date_of_birth) {
      formattedPatient.age = calculateAge(patientData.date_of_birth);
    }
    
    return formattedPatient;
  };
  
  const updatePatientProfile = async (data: any) => {
    if (!patient.profile_id) {
      toast({
        title: "Error",
        description: "Cannot update patient: missing profile ID",
        variant: "destructive"
      });
      return false;
    }
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', patient.profile_id);
        
      if (error) throw error;
      
      // Update local state
      setPatient(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          ...data
        }
      }));
      
      toast({
        title: "Profile updated",
        description: "Patient profile has been updated successfully"
      });
      
      return true;
    } catch (error: any) {
      console.error('Error updating patient profile:', error);
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive"
      });
      return false;
    }
  };
  
  const updatePatientDetails = async (data: any) => {
    if (!patient.id) {
      toast({
        title: "Error",
        description: "Cannot update patient: missing patient ID",
        variant: "destructive"
      });
      return false;
    }
    
    try {
      const { error } = await supabase
        .from('patients')
        .update(data)
        .eq('id', patient.id);
        
      if (error) throw error;
      
      // Update local state
      setPatient(prev => ({
        ...prev,
        ...data
      }));
      
      toast({
        title: "Patient updated",
        description: "Patient details have been updated successfully"
      });
      
      return true;
    } catch (error: any) {
      console.error('Error updating patient details:', error);
      toast({
        title: "Error updating patient",
        description: error.message,
        variant: "destructive"
      });
      return false;
    }
  };
  
  return {
    patient,
    isLoading,
    error,
    updatePatientProfile,
    updatePatientDetails,
    calculateAge,
    refetchPatient: () => patientId && fetchPatientData(patientId)
  };
}
